import React from "react";
import { CheckCircle2 } from "lucide-react";
import { BRAND } from "../lib/brand";

const PRINCIPLES = [
  "Hand-selected veneers from responsibly managed forests.",
  "Multi-stage seasoning to prevent warping and cracks.",
  "Pest, borer & termite treated at the core layer.",
  "Pressed with consistent heat and pressure for strength.",
  "Final inspection on every sheet before it leaves our line.",
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="paper-grain">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
                About Sylva King
              </div>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-wood-800 leading-[1.03] mt-4">
                A name carved<br />in good timber.
              </h1>
            </div>
            <div className="lg:col-span-5">
              <p className="text-lg text-wood-700 leading-relaxed">
                Sylva King Plywood is built on a simple belief — that the
                foundation of any beautiful space begins with honest material.
                We craft plywood the way it was meant to be: patiently, precisely,
                and proudly.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-wood-100 py-12 sm:py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8">
          <div className="img-zoom card-flat rounded-sm overflow-hidden bg-white">
            <img
              src={BRAND.images.aboutUs}
              alt="From Forest to Finish — the Sylva King plywood process"
              className="w-full h-auto object-contain"
              data-testid="about-image"
            />
          </div>
        </div>
      </section>

      <section className="bg-wood-100 pb-20 lg:pb-28">
        <div className="mx-auto max-w-3xl px-6 sm:px-8">
          <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
            Our Story
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl text-wood-800 mt-3">
            From forest to finish.
          </h2>
          <p className="mt-6 text-wood-700 leading-relaxed">
            India has always had a deep relationship with wood — in our temples,
            our homes, our heirlooms. Sylva King carries forward that legacy.
            Our team works with seasoned veneer experts, modern hot-press
            facilities and an obsession for detail that runs through every layer.
          </p>
          <p className="mt-4 text-wood-700 leading-relaxed">
            The crown in our mark is a quiet promise: every Sylva King sheet
            earns its name through strength, stability and a finish that lasts
            decades, not seasons.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-sm sm:text-base uppercase tracking-[0.18em] text-gold-600 font-bold">
              What we stand for
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl text-wood-800 mt-3 leading-[1.05]">
              Five principles.<br />Every sheet.
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-5">
            {PRINCIPLES.map((p, i) => (
              <li
                key={i}
                data-testid={`principle-${i}`}
                className="card-flat rounded-sm p-6 flex gap-4 items-start"
              >
                <CheckCircle2 className="text-gold-600 shrink-0 mt-0.5" size={20} />
                <span className="text-wood-700">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
